import { memo, useCallback, useMemo } from 'react';
import { RackingArea } from '@thive/fddm-types';

import { SelectInput } from '@/modules/common/components/inputs';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useTranslatedOptions } from '@/modules/common/hooks';

const LOAD_HANDLING_TYPE_OPTIONS: {
  labelKey: string;
  value: RackingArea['rackingLoadHandlingType'];
}[] = [
  { labelKey: 'interface:properties.area.load_handling_type_options.intake', value: 'INTAKE' },
  { labelKey: 'interface:properties.area.load_handling_type_options.storage', value: 'STORAGE' },
  { labelKey: 'interface:properties.area.load_handling_type_options.delivery', value: 'DELIVERY' },
];

interface Props {
  selectedRackingAreas: RackingArea[];
}

function RackingLoadHandlingTypeComponent({ selectedRackingAreas }: Props) {
  const loadHandlingTypeOptions = useTranslatedOptions(LOAD_HANDLING_TYPE_OPTIONS);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const rackingLoadHandlingType = useMemo(
    () => getCommonOption(selectedRackingAreas.map((area) => area.rackingLoadHandlingType)),
    [selectedRackingAreas],
  );

  const onChange = useCallback(
    (newValue: RackingArea['rackingLoadHandlingType']) => {
      fddmSvc.setPreference('rackingArea.rackingLoadHandlingType', newValue);

      const updatedAreas = createEmptyAreas();
      selectedRackingAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, { ...area, rackingLoadHandlingType: newValue });
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [selectedRackingAreas, fddmSvc],
  );

  return (
    <SelectInput
      options={loadHandlingTypeOptions}
      onChange={onChange}
      value={rackingLoadHandlingType}
      showMixed
    />
  );
}

const RackingLoadHandlingType = memo(RackingLoadHandlingTypeComponent);

export default RackingLoadHandlingType;
